const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

const addressLines = (a) => {
  if (!a) return "<p class='muted'>No shipping address on file</p>";
  return `
    <p class="addr-name">${esc(a.name)}</p>
    <p>${esc(a.line1)}${a.line2 ? ", " + esc(a.line2) : ""}</p>
    <p class="addr-city">${esc(a.city)}, ${esc(a.province)}&nbsp;&nbsp;${esc(a.postal_code)}</p>
    <p>${esc(a.country || "Canada").toUpperCase()}</p>
    ${a.phone ? `<p class="muted">Tel: ${esc(a.phone)}</p>` : ""}`;
};

const openPrintWindow = (html) => {
  const w = window.open("", "_blank", "width=900,height=1100");
  if (!w) return false;
  w.document.write(html);
  w.document.close();
  w.focus();
  return true;
};

const AUTOPRINT = "<script>window.addEventListener('load', function(){ setTimeout(function(){ window.print(); }, 300); });</script>";

const isExpress = (order) => order.shipping_method === "express";
const shipMethodText = (order) => (isExpress(order) ? "EXPRESS — Canada Post Xpresspost" : "Standard — Canada Post Expedited Parcel");

// 57mm x 32mm thermal label (Munbyn-compatible)
export function printShippingLabel(order, shipFrom) {
  const a = order.shipping_address;
  const fromLine = (shipFrom || "Eternal Biolabs").split("\n")[0];
  const unit = String(a?.line2 || "").trim().replace(/^(unit|suite|apt\.?|apartment|#)\s*/i, "");
  const addr = a
    ? `<p class="hdr">SHIP TO</p>
       <p class="name">${esc(a.name)}</p>
       <p>${unit ? `#${esc(unit)} - ` : ""}${esc(a.line1)}</p>
       <p class="city">${esc(a.city)}, ${esc(a.province)} ${esc(a.postal_code)}</p>
       <p class="country">${esc(a.country || "Canada").toUpperCase()}</p>`
    : "<p>No shipping address on file</p>";

  const html = `<!DOCTYPE html>
<html>
<head>
<title>${esc(order.order_number)} — Shipping Label</title>
<style>
  @page { size: 57mm 32mm; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #000; }
  .label { width: 57mm; height: 32mm; padding: 1.5mm 2mm; display: flex; flex-direction: column; overflow: hidden; background: #fff; }
  .to { flex: 1; line-height: 1.2; overflow: hidden; }
  .to p { font-size: 7.5pt; }
  .to .hdr { font-size: 5.5pt; letter-spacing: 1.5px; font-weight: bold; color: #333; }
  .to .name { font-size: 13pt; font-weight: bold; line-height: 1.05; margin: 0.4mm 0 0.5mm 0; }
  .to .city { font-weight: bold; font-size: 8.5pt; }
  .to .country { font-size: 7pt; letter-spacing: 0.5px; }
  .foot { display: flex; justify-content: space-between; align-items: baseline; border-top: 0.3mm solid #000; padding-top: 0.6mm; }
  .foot .num { font-size: 7pt; font-weight: bold; }
  .foot .from { font-size: 5pt; color: #333; text-align: right; max-width: 32mm; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  @media screen {
    html, body { width: 100%; height: 100%; }
    body { background: #3A3835; display: flex; align-items: center; justify-content: center; }
    .print-btn { position: fixed; top: 12px; right: 12px; background: #fff; color: #1A1A1A; border: none; border-radius: 999px; padding: 10px 22px; font-size: 14px; font-weight: bold; letter-spacing: 1px; cursor: pointer; }
  }
  @media print {
    html, body { width: 57mm; height: 32mm; background: #fff; display: block; }
    .label { transform: none !important; }
    .print-btn { display: none; }
  }
</style>
</head>
<body>
  <div class="label">
    <div class="to">${addr}</div>
    <div class="foot">
      <span class="num">${esc(order.order_number)}${isExpress(order) ? " · XPRESS" : ""}</span>
      <span class="from">FROM: ${esc(fromLine)}</span>
    </div>
  </div>
  <button class="print-btn" onclick="window.print()">PRINT</button>
  <script>
    (function () {
      function fit() {
        var el = document.querySelector(".label");
        var s = Math.min((window.innerWidth * 0.94) / el.offsetWidth, (window.innerHeight * 0.88) / el.offsetHeight);
        el.style.transform = "scale(" + s + ")";
      }
      window.addEventListener("load", fit);
      window.addEventListener("resize", fit);
      fit();
    })();
  </script>
</body>
</html>`;
  return openPrintWindow(html);
}

export function printPackingSlip(order) {
  const itemRows = order.items
    .map((i, idx) => `<tr><td>${idx + 1}</td><td>${esc(i.name)}</td><td>${esc(i.dosage)}</td><td class="qty">${i.quantity}</td></tr>`)
    .join("");
  const totalUnits = order.items.reduce((s, i) => s + i.quantity, 0);
  const date = new Date(order.created_at).toLocaleDateString("en-CA");

  const html = `<!DOCTYPE html>
<html>
<head>
<title>${esc(order.order_number)} — Packing Slip</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #000; }
  .page { padding: 0.5in; }
  .muted { color: #555; }
  .slip-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #000; padding-bottom: 0.15in; }
  .brand { font-size: 20pt; font-weight: bold; letter-spacing: 4px; }
  .doc-title { font-size: 11pt; letter-spacing: 3px; margin-top: 4px; }
  .slip-meta { text-align: right; font-size: 10pt; line-height: 1.6; }
  .slip-to { margin-top: 0.25in; font-size: 11pt; line-height: 1.5; }
  .slip-to .hdr { font-size: 9pt; letter-spacing: 2px; font-weight: bold; color: #555; }
  .slip-to .addr-name { font-size: 12pt; font-weight: bold; }
  .addr-city { font-weight: bold; }
  table { width: 100%; border-collapse: collapse; margin-top: 0.3in; font-size: 10.5pt; }
  th { text-align: left; border-bottom: 2px solid #000; padding: 6px 8px; font-size: 9pt; letter-spacing: 1px; }
  td { border-bottom: 1px solid #ccc; padding: 7px 8px; }
  th.qty, td.qty { text-align: right; width: 60px; }
  tfoot td { border-bottom: none; border-top: 2px solid #000; font-weight: bold; }
  .slip-foot { margin-top: 0.4in; font-size: 9pt; color: #555; line-height: 1.6; }
</style>
</head>
<body>
  <div class="page">
    <div class="slip-header">
      <div>
        <p class="brand">ETERNAL BIOLABS</p>
        <p class="doc-title">PACKING SLIP</p>
      </div>
      <div class="slip-meta">
        <p><strong>Order:</strong> ${esc(order.order_number)}</p>
        <p><strong>Date:</strong> ${date}</p>
        <p><strong>Customer:</strong> ${esc(order.customer_name || order.email)}</p>
        <p><strong>Shipping:</strong> ${isExpress(order) ? "<strong>" : ""}${shipMethodText(order)}${isExpress(order) ? "</strong>" : ""}</p>
      </div>
    </div>
    <div class="slip-to">
      <p class="hdr">SHIP TO</p>
      ${addressLines(order.shipping_address)}
    </div>
    <table>
      <thead><tr><th>#</th><th>ITEM</th><th>DOSAGE</th><th class="qty">QTY</th></tr></thead>
      <tbody>${itemRows}</tbody>
      <tfoot><tr><td></td><td colspan="2">TOTAL UNITS</td><td class="qty">${totalUnits}</td></tr></tfoot>
    </table>
    <div class="slip-foot">
      <p>Please verify the contents of this package against the list above.</p>
      <p>All products are for research purposes only. Store lyophilized peptides at 2–8°C.</p>
      <p>Questions? Reply to your order confirmation email.</p>
    </div>
  </div>
${AUTOPRINT}
</body>
</html>`;
  return openPrintWindow(html);
}
